import type { TradingNavigatorParamList } from './types';

import { Platform, Pressable, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { useMedia } from 'tamagui';

import { Icon, getTestProps } from '@south-street-app/atoms';
import {
  TradeOrderScreen,
  PreviewTradeOrderScreen,
  TradeConfirmationScreen,
  TradeTimeoutScreen,
  FirmSelectClientsScreen,
} from '@south-street-app/screens';
import { useGetAnalyticFirmAssociates } from '@utility-nyc/react-query-sdk';

import { TradeOrderHeader } from './TradeOrderHeader';

const { Navigator, Screen } = createStackNavigator<TradingNavigatorParamList>();

const styles = StyleSheet.create({
  headerLeft: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    height: 40,
    justifyContent: 'center',
    paddingLeft: 8,
    width: 48,
  },
  pressStyle: {
    opacity: 0.7,
  },
});

const HeaderLeft = ({ isBackButton = false }: { isBackButton?: boolean }) => {
  const { goBack } = useNavigation();

  return (
    <Pressable
      accessibilityLabel={isBackButton ? 'Go back' : 'Close'}
      accessibilityRole={'button'}
      hitSlop={8}
      onPress={goBack}
      style={({ pressed }) => [
        styles.headerLeft,
        pressed ? styles.pressStyle : undefined,
        Platform.OS === 'web' ? { cursor: 'pointer' } : undefined,
      ]}
      {...getTestProps(
        isBackButton ? 'navigation-back-button' : 'navigation-close-button',
      )}
    >
      <Icon
        color={'$mono700'}
        size={'$6'}
        iconName={isBackButton ? 'chevronLeftIcon' : 'closeIcon'}
      />
    </Pressable>
  );
};

const HeaderLeftClose = () => HeaderLeft({ isBackButton: false });
const HeaderLeftBack = () => HeaderLeft({ isBackButton: true });
const nullHeader = () => null;

const TradingStackNavigator = () => {
  const { data } = useGetAnalyticFirmAssociates();

  const isFirmTrade = (data?.data.associates ?? []).length >= 1;

  const { desktop } = useMedia();

  return (
    <Navigator
      initialRouteName={isFirmTrade ? 'FirmSelectClientsScreen' : 'TradeOrder'}
      screenOptions={{
        ...(desktop && {
          cardStyle: { flex: 1 },
        }),
        headerShown: true,
      }}
    >
      <Screen
        name={'TradeOrder'}
        options={{
          headerTitle: TradeOrderHeader,
          headerLeft: HeaderLeftClose,
          ...(desktop ? { headerTitleAlign: 'center' } : undefined),
          headerStyle: {
            shadowColor: 'transparent',
            elevation: 0,
          },
        }}
        component={TradeOrderScreen}
      />
      <Screen
        name={'FirmSelectClientsScreen'}
        options={{
          headerShown: false,
        }}
        component={FirmSelectClientsScreen}
      />
      <Screen
        name={'PreviewTradeOrder'}
        options={{
          headerTitle: 'Trade Order',
          headerLeft: HeaderLeftBack,
          ...(desktop ? { headerTitleAlign: 'center' } : undefined),
          headerStyle: {
            shadowColor: 'transparent',
            elevation: 0,
          },
        }}
        component={PreviewTradeOrderScreen}
      />
      <Screen
        name={'TradeConfirmation'}
        options={{
          headerTitle: 'Trade Information',
          ...(desktop ? { headerTitleAlign: 'center' } : undefined),
          headerStyle: {
            shadowColor: 'transparent',
            elevation: 0,
          },
          headerLeft: nullHeader,
        }}
        component={TradeConfirmationScreen}
      />
      <Screen
        name={'TradeTimeout'}
        options={{ headerShown: false }}
        component={TradeTimeoutScreen}
      />
    </Navigator>
  );
};

export { TradingStackNavigator };
